import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../lib/api";
import {
  ExecModal, ExecRow, ChannelBadge,
  execMeta, fmtDate, fmtDuration, typeLabel, PulsingDot,
} from "../shared/ExecModal";

interface ExecPage {
  items: ExecRow[];
  next_cursor: string | null;
}

// ── TabExecucoes ──────────────────────────────────────────────────────────────
//
// Modo agente:  agent={{ id, name }} → mostra execuções de um agente específico.
// Modo global:  sem prop agent     → mostra execuções de todos os agentes (dashboard).

interface TabExecucoesProps {
  agent?: { id: string; name?: string };
}

// Todo filtro (principal/detalhado, status, tipo, canal, data) roda no SQL —
// exec_filters.py na API. Filtrar aqui em cima de página paginada escondia resultado.

export function TabExecucoes({ agent }: TabExecucoesProps) {
  const isGlobal = !agent;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [canalFilter, setCanalFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [detailed, setDetailed] = useState(false);

  const entityKey = isGlobal ? "__global__" : agent!.id;
  const queryKey = ["executions", entityKey, { detailed, statusFilter, tipoFilter, canalFilter, dateFilter }];

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<ExecPage>({
    queryKey,
    queryFn: ({ pageParam }) => {
      const endpoint = isGlobal
        ? "/dashboard/executions"
        : `/agents/${entityKey}/executions`;
      // ponytail: filtrou → vem tudo numa tacada, teto de 1000 por página (é o le= da API);
      // passou disso o "Carregar mais" traz os próximos 1000. O polling re-busca essa página
      // inteira — se pesar em filtro largo (Sucesso/Conversa), subir o refetchInterval com filtro.
      const p = new URLSearchParams({ limit: statusFilter || tipoFilter || canalFilter ? "1000" : "50" });
      if (!detailed) p.set("simplified", "true");
      if (statusFilter) p.set("status", statusFilter);
      if (tipoFilter) p.set("types", tipoFilter);
      if (canalFilter) p.set("channel", canalFilter);
      if (dateFilter) p.set("until", new Date(dateFilter).toISOString());
      if (pageParam) p.set("cursor", String(pageParam));
      return api.get<ExecPage>(`${endpoint}?${p}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      const hasRunning = pages?.some(p => p.items.some(e => e.status === "running"));
      return hasRunning ? 2000 : 5000;
    },
  });

  const executions: ExecRow[] = data?.pages.flatMap(p => p.items) ?? [];

  const selectedExec = executions.find(e => e.id === selectedId) ?? null;

  const qc = useQueryClient();

  function patchRowStatus(id: string, status: string) {
    qc.setQueryData<{ pages: ExecPage[]; pageParams: unknown[] }>(
      queryKey,
      old => old && {
        ...old,
        pages: old.pages.map(p => ({
          ...p,
          items: p.items.map(it => (it.id === id ? { ...it, status } : it)),
        })),
      },
    );
  }

  async function handleCancel(exec: ExecRow) {
    try {
      const r = await api.post<{ cancelled: boolean; status: string }>(
        `/agents/${exec.agent_id}/executions/${exec.id}/cancel`, {},
      );
      if (r.cancelled) {
        patchRowStatus(exec.id, "cancelled");  // feedback imediato
      } else {
        patchRowStatus(exec.id, r.status);
        alert(`Esta execução já havia finalizado (${execMeta(r.status).label}).`);
      }
    } catch {
      alert("Falha ao cancelar — tente novamente.");
    }
    refetch();
  }

  function stepsUrl(exec: ExecRow) {
    return isGlobal
      ? `/dashboard/executions/${exec.id}/steps`
      : `/agents/${agent!.id}/executions/${exec.id}/steps`;
  }

  // column headers — add AGENTE column in global mode
  const headers = isGlobal
    ? ["STATUS", "CANAL", "AGENTE", "TIPO", "INICIADO", "DURAÇÃO", "ID", ""]
    : ["STATUS", "CANAL", "INICIADO", "DURAÇÃO", "ID", "TIPO", ""];

  return (
    // No dashboard (global) quem rola é a página inteira; na aba do agente a tabela rola sozinha.
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: isGlobal ? "visible" : "hidden" }}>
      <style>{`@keyframes execModalIn{from{opacity:0;transform:scale(0.96) translateY(6px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>

      {/* ── Toolbar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "14px 24px", borderBottom: "1px solid var(--surface-input)", flexShrink: 0,
      }}>
        {!isGlobal && <span style={{ fontSize: 13, fontWeight: 600, marginRight: 4 }}>Execuções</span>}

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 8,
            padding: "6px 12px", fontSize: 12, color: "var(--ink)", cursor: "pointer",
          }}
        >
          <option value="">Todos os status</option>
          <option value="sucesso">Sucesso</option>
          <option value="erro">Erro</option>
          <option value="fallback">Fallback</option>
          <option value="janela">Janela fechada</option>
          <option value="rodando">Rodando</option>
          <option value="cancelada">Cancelada</option>
          <option value="reset">Reset</option>
          <option value="white">White bloqueado</option>
          <option value="black">Black bloqueado</option>
          <option value="ai_paused">IA Pausada</option>
        </select>

        <select
          value={tipoFilter}
          onChange={e => setTipoFilter(e.target.value)}
          style={{
            background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 8,
            padding: "6px 12px", fontSize: 12, color: "var(--ink)", cursor: "pointer",
          }}
        >
          <option value="">Todos os tipos</option>
          <option value="process_message">Conversa</option>
          <option value="followup">Follow-up</option>
          <option value="patioo">Patioo</option>
          <option value="portal">Portal (e-mail)</option>
          <option value="call_summary">Ligação</option>
          <option value="webhook">Webhook</option>
        </select>

        <select
          value={canalFilter}
          onChange={e => setCanalFilter(e.target.value)}
          style={{
            background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 8,
            padding: "6px 12px", fontSize: 12, color: "var(--ink)", cursor: "pointer",
          }}
        >
          <option value="">Todos os canais</option>
          <option value="waba">WhatsApp</option>
          <option value="externo">WhatsApp (Externa)</option>
          <option value="instagram">Instagram</option>
          <option value="messenger">Messenger</option>
        </select>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--ink-muted)", whiteSpace: "nowrap" }}>Até</span>
          <input
            type="datetime-local"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            style={{
              background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 6,
              padding: "6px 10px", fontSize: 12, color: "var(--ink)",
              
            }}
          />
          <button
            onClick={() => setDateFilter("")}
            style={{ fontSize: 11, color: "var(--ink-muted)", padding: "3px 6px", borderRadius: 4, background: "none", border: "none", cursor: "pointer" }}
            onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")}
            onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}
          >
            Limpar
          </button>
        </div>

        {/* Toggle detalhado */}
        <button
          onClick={() => setDetailed(v => !v)}
          style={{
            marginLeft: "auto", display: "flex", alignItems: "center", gap: 7,
            fontSize: 12, color: detailed ? "var(--ink)" : "var(--ink-muted)",
            padding: "5px 10px", borderRadius: 8, cursor: "pointer",
            border: `1px solid ${detailed ? "var(--brand)" : "var(--line-strong)"}`,
            background: detailed ? "var(--brand-soft)" : "transparent",
            transition: "all 0.15s",
          }}
        >
          {/* toggle pill */}
          <div style={{
            width: 28, height: 16, borderRadius: 99, position: "relative",
            background: detailed ? "var(--brand)" : "var(--line-strong)",
            transition: "background 0.15s", flexShrink: 0,
          }}>
            <div style={{
              position: "absolute", top: 2,
              left: detailed ? 14 : 2,
              width: 12, height: 12, borderRadius: "50%",
              background: "#fff", transition: "left 0.15s",
            }} />
          </div>
          Detalhado
        </button>

        <button
          onClick={() => refetch()}
          style={{
            fontSize: 12, color: "var(--ink-secondary)",
            padding: "6px 12px", borderRadius: 8,
            border: "1px solid var(--line-strong)", display: "flex", alignItems: "center", gap: 5,
            background: "transparent", cursor: "pointer",
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
          </svg>
          Atualizar
        </button>
      </div>

      {/* ── Table ── */}
      <div style={{ flex: 1, overflowY: isGlobal ? "visible" : "auto" }}>
        {isLoading ? (
          <div style={{ padding: 24, color: "var(--ink-dim)", fontSize: 13 }}>Carregando...</div>
        ) : executions.length === 0 ? (
          <div style={{ padding: 24, color: "var(--ink-dim)", fontSize: 13 }}>Nenhuma execução encontrada.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--surface-app)", zIndex: 1 }}>
              <tr style={{ borderBottom: "1px solid var(--surface-input)" }}>
                {headers.map((h, i) => (
                  <th key={i} style={{
                    padding: `10px ${i === 0 ? "24px" : "16px"}`,
                    fontSize: 11, fontWeight: 600, color: "var(--ink-muted)",
                    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "left",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {executions.map(exec => {
                const m = execMeta(exec.status);
                const isRunning = exec.status === "running";
                const cancelable = exec.status === "running" || exec.status === "waiting_agent";
                return (
                  <tr
                    key={exec.id}
                    onClick={() => setSelectedId(exec.id)}
                    style={{ borderBottom: "1px solid var(--surface-hover)", cursor: "pointer", transition: "background 0.1s" }}
                    onMouseOver={e => (e.currentTarget.style.background = "var(--surface-hover)")}
                    onMouseOut={e => (e.currentTarget.style.background = "")}
                  >
                    {/* STATUS — sempre presente */}
                    <td style={{ padding: "12px 24px" }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: 12, fontWeight: 500, padding: "3px 10px", borderRadius: 99,
                        background: m.bg, color: m.color,
                      }}>
                        {isRunning
                          ? <PulsingDot color={m.color} />
                          : <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                        }
                        {m.label}
                      </span>
                    </td>

                    {/* CANAL — logo à direita do status */}
                    <td style={{ padding: "12px 16px" }}>
                      <ChannelBadge channel={exec.channel} />
                    </td>

                    {/* AGENTE — somente no modo global */}
                    {isGlobal && (
                      <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 500 }}>
                        {exec.agent_name ?? exec.agent_id.slice(0, 8)}
                      </td>
                    )}

                    {/* TIPO — no modo global vem antes de INICIADO */}
                    {isGlobal && (
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{
                          fontSize: 11, padding: "2px 8px", borderRadius: 4,
                          background: "var(--surface-input)", border: "1px solid var(--line-strong)", color: "var(--ink-muted)",
                        }}>
                          {typeLabel(exec.type)}
                        </span>
                      </td>
                    )}

                    {/* INICIADO */}
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--ink-secondary)", whiteSpace: "nowrap" }}>
                      {fmtDate(exec.started_at)}
                    </td>

                    {/* DURAÇÃO */}
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--ink-secondary)" }}>
                      {fmtDuration(exec.duration_ms)}
                    </td>

                    {/* ID */}
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "var(--ink-dim)", fontFamily: "var(--font-mono)" }}>
                      {exec.id.slice(0, 8)}
                    </td>

                    {/* TIPO — no modo agente vem depois do ID */}
                    {!isGlobal && (
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{
                          fontSize: 11, padding: "2px 8px", borderRadius: 4,
                          background: "var(--surface-input)", border: "1px solid var(--line-strong)", color: "var(--ink-muted)",
                        }}>
                          {typeLabel(exec.type)}
                        </span>
                      </td>
                    )}

                    {/* cancelar + chevron */}
                    <td style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {cancelable && (
                        <button
                          onClick={e => { e.stopPropagation(); handleCancel(exec); }}
                          style={{
                            fontSize: 11, color: "var(--ink-secondary)", marginRight: 12,
                            padding: "3px 10px", borderRadius: 6,
                            border: "1px solid var(--line-strong)", background: "transparent", cursor: "pointer",
                          }}
                          onMouseOver={e => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
                          onMouseOut={e => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
                        >
                          Cancelar
                        </button>
                      )}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--ink-dim)", verticalAlign: "middle" }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Carregar mais ── */}
      {hasNextPage && (
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--surface-input)", textAlign: "center" }}>
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            style={{
              fontSize: 12, color: isFetchingNextPage ? "var(--ink-dim)" : "var(--ink-secondary)",
              padding: "6px 20px", borderRadius: 8,
              border: "1px solid var(--line-strong)", background: "transparent", cursor: isFetchingNextPage ? "default" : "pointer",
            }}
            onMouseOver={e => { if (!isFetchingNextPage) { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}}
            onMouseOut={e => { if (!isFetchingNextPage) { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}}
          >
            {isFetchingNextPage ? "Carregando..." : "Carregar mais"}
          </button>
        </div>
      )}

      {/* ── Modal ── */}
      {selectedExec && (
        <ExecModal
          exec={selectedExec}
          stepsUrl={stepsUrl(selectedExec)}
          subtitle={isGlobal ? selectedExec.agent_name : undefined}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
